import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, RotateCcw } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

/**
 * FIELD-001C2A · Operations-only canonical task reopen dialog.
 *
 * Enforced client-side + server-side:
 *   • Requires a non-empty reason (persisted on the audit row + on the
 *     task's `reopen_history`).
 *   • Preserves assignees + required_resource_type — nothing is
 *     silently cleared. The backend clears completion metadata so
 *     workload / dashboard counts recover immediately.
 *   • Refuses to close on a failed save; the operator's reason and
 *     the dialog state are retained, and the API error is surfaced.
 *   • Field-role callers never reach this UI: the surrounding Reopen
 *     button only renders for `manage_projects`. The server enforces
 *     the same via `require_permission(MANAGE_PROJECTS)`.
 */
export default function TaskReopenDialog({ projectId, task, variant = "canonical", onClose, onReopened }) {
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const trimmed = (reason || "").trim();
    const canSubmit = trimmed.length > 0 && !saving;

    const submit = async () => {
        if (!canSubmit) return;
        setSaving(true);
        setError("");
        try {
            // FIELD-001C2B · Ad-hoc reopens go through the dedicated
            // `POST /api/tasks/{id}/reopen` endpoint; canonical
            // reopens stay on the project-scoped endpoint.
            const updated = variant === "adhoc"
                ? await api.reopenAdHocTask(task.id, trimmed)
                : await api.reopenPhaseTask(projectId, task.id, trimmed);
            toast.success("Task reopened");
            onReopened?.(updated);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const msg = (typeof detail === "object" ? detail?.message : null)
                     || formatApiError(detail)
                     || "Could not reopen task";
            setError(msg);
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onOpenChange={(v) => !v && !saving && onClose?.()}>
            <DialogContent
                className="max-w-md"
                data-testid="ops-task-reopen-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-[#131B2F]">
                        <RotateCcw size={16} strokeWidth={1.75} className="text-[#131B2F]" />
                        Reopen task
                    </DialogTitle>
                    <div className="text-xs text-[#4B5565] mt-1"
                         data-testid="ops-task-reopen-task-name">
                        {task?.name}
                    </div>
                </DialogHeader>

                <div className="mt-3">
                    <label htmlFor="ops-task-reopen-reason"
                           className="block text-[11px] text-[#4B5565] mb-1">
                        Reason (required) — recorded on the audit trail
                    </label>
                    <textarea
                        id="ops-task-reopen-reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={3}
                        maxLength={500}
                        placeholder="Why is this task being reopened?"
                        disabled={saving}
                        data-testid="ops-task-reopen-reason"
                        className="w-full rounded-md border border-[#E5E7EB] bg-white px-3 py-2 text-sm text-[#131B2F] focus:outline-none focus:ring-2 focus:ring-[#131B2F]/10 focus:border-[#131B2F] disabled:opacity-60"
                    />
                    <div className="text-[11px] text-[#4B5565] mt-1">
                        Assignees and the required resource type stay attached
                        to the task. The previous completion time is preserved
                        in the task’s reopen history.
                    </div>
                </div>

                {error && (
                    <div className="mt-2 text-xs text-[#DA3535]"
                         data-testid="ops-task-reopen-error">
                        {error}
                    </div>
                )}

                <DialogFooter className="mt-3">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        data-testid="ops-task-reopen-cancel"
                        className="pe-btn-outline h-10 disabled:opacity-60"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={!canSubmit}
                        data-testid="ops-task-reopen-submit"
                        className="pe-btn-primary h-10 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {saving ? (<><Loader2 size={14} className="animate-spin" /> Reopening…</>) : "Reopen task"}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
